import React from 'react';
import { useCurrentFrame, interpolate, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { AnimatedText } from '../components/AnimatedText';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

const DUR = SCENES.MONITOR.duration;

export const MonitorScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 15, DUR], [1, 0], { extrapolateRight: 'clamp' });

  // Price chart animation
  const chartProgress = interpolate(frame, [30, 180], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const yourPrices = [80, 78, 78, 76, 74, 72, 70, 70, 68, 68, 66, 65];
  const compPrices = [92, 88, 85, 82, 80, 78, 75, 73, 72, 70, 68, 67];
  const chartW = 520;
  const chartH = 160;
  const buildPath = (p: number[]) =>
    p
      .map((v, i) => `${(i / (p.length - 1)) * chartW},${chartH - ((v - 55) / 45) * chartH}`)
      .join(' L ');

  // Competitor price table
  const competitors = [
    {
      name: 'Amazon',
      product: 'Laptop HP 15"',
      yours: '$899',
      theirs: '$849',
      diff: '-5.6%',
      trend: 'down',
    },
    {
      name: 'MercadoLibre',
      product: 'Monitor LG 27"',
      yours: '$420',
      theirs: '$398',
      diff: '-5.2%',
      trend: 'down',
    },
    {
      name: 'Shopify Store',
      product: 'Teclado Mech.',
      yours: '$85',
      theirs: '$92',
      diff: '+8.2%',
      trend: 'up',
    },
  ];

  const tableReveal = (i: number) =>
    interpolate(frame, [60 + i * 15, 75 + i * 15], [0, 1], {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    });

  // Import container
  const fill = interpolate(frame, [140, 230], [0, 82], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const importCosts = [
    { label: 'FOB', value: '$38,200', pct: '' },
    { label: 'Flete marítimo', value: '$3,400', pct: '8.9%' },
    { label: 'Seguro', value: '$580', pct: '1.5%' },
    { label: 'Arancel', value: '$2,292', pct: '6%' },
    { label: 'Despacho', value: '$808', pct: '2.1%' },
  ];

  const costReveal = (i: number) =>
    interpolate(frame, [160 + i * 8, 172 + i * 8], [0, 1], {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    });
  const totalReveal = interpolate(frame, [210, 225], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="cool" />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 60px',
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <AnimatedText
            text="Monitoreo Total"
            fontSize={44}
            fontWeight={800}
            mode="scaleIn"
            startFrame={3}
          />
        </div>

        <div style={{ display: 'flex', gap: 20, width: '100%' }}>
          {/* Competitor Pricing Panel */}
          <div
            style={{
              flex: 1.2,
              background: `${COLORS.bgLight}BB`,
              border: `1px solid ${COLORS.primary}15`,
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>💰</span> Pricing Dinámico
            </div>

            {/* Price chart */}
            <svg width={chartW} height={chartH + 16} viewBox={`0 0 ${chartW} ${chartH + 16}`}>
              <defs>
                <clipPath id="mc">
                  <rect x={0} y={0} width={chartProgress * chartW} height={chartH + 16} />
                </clipPath>
              </defs>
              <g clipPath="url(#mc)">
                <path
                  d={`M ${buildPath(compPrices)}`}
                  fill="none"
                  stroke={COLORS.amber}
                  strokeWidth={2}
                  opacity={0.6}
                  strokeDasharray="4 3"
                />
                <path
                  d={`M ${buildPath(yourPrices)}`}
                  fill="none"
                  stroke={COLORS.emerald}
                  strokeWidth={3}
                  style={{ filter: `drop-shadow(0 0 4px ${COLORS.emerald}60)` }}
                />
              </g>
            </svg>
            <div style={{ display: 'flex', gap: 16, marginTop: 4, marginBottom: 10 }}>
              {[
                { l: 'Tu precio', c: COLORS.emerald },
                { l: 'Competencia', c: COLORS.amber },
              ].map((i) => (
                <div
                  key={i.l}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 10,
                    color: COLORS.gray,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <div style={{ width: 12, height: 3, borderRadius: 2, backgroundColor: i.c }} />{' '}
                  {i.l}
                </div>
              ))}
            </div>

            {/* Competitor Table */}
            <div style={{ borderTop: `1px solid ${COLORS.grayDark}30`, paddingTop: 8 }}>
              {competitors.map((c, i) => (
                <div
                  key={i}
                  style={{
                    opacity: tableReveal(i),
                    display: 'flex',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderTop: i > 0 ? `1px solid ${COLORS.grayDark}15` : 'none',
                    fontSize: 11,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: COLORS.white }}>{c.name}</div>
                    <div style={{ fontSize: 9, color: COLORS.grayDark }}>{c.product}</div>
                  </div>
                  <div style={{ width: 60, color: COLORS.gray, textAlign: 'right' }}>{c.yours}</div>
                  <div
                    style={{ width: 60, color: COLORS.white, fontWeight: 600, textAlign: 'right' }}
                  >
                    {c.theirs}
                  </div>
                  <div
                    style={{
                      width: 50,
                      fontWeight: 700,
                      textAlign: 'right',
                      color: c.trend === 'down' ? COLORS.red : COLORS.emerald,
                    }}
                  >
                    {c.diff}
                  </div>
                </div>
              ))}
            </div>

            {/* Alert */}
            <div
              style={{
                marginTop: 8,
                padding: '6px 12px',
                background: `${COLORS.amber}12`,
                border: `1px solid ${COLORS.amber}30`,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: COLORS.amber,
                fontFamily: 'Inter, system-ui, sans-serif',
                opacity: interpolate(frame, [100, 115], [0, 1], {
                  extrapolateRight: 'clamp',
                  extrapolateLeft: 'clamp',
                }),
              }}
            >
              ⚠️ Competidor X bajó 12% en Laptop HP 15"
            </div>
          </div>

          {/* Import Tracking Panel */}
          <div
            style={{
              flex: 0.8,
              background: `${COLORS.bgLight}BB`,
              border: `1px solid ${COLORS.primary}15`,
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>🚢</span> Importación #IM-2847
            </div>

            {/* Container fill visual */}
            <div
              style={{
                width: '100%',
                height: 100,
                border: `1px solid ${COLORS.primary}40`,
                borderRadius: 8,
                position: 'relative',
                overflow: 'hidden',
                background: `${COLORS.bg}80`,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: `${fill}%`,
                  background: `linear-gradient(180deg, ${COLORS.brandTeal}30, ${COLORS.brandTeal}10)`,
                  borderTop: `2px solid ${COLORS.brandTeal}50`,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%,-50%)',
                  fontSize: 24,
                  fontWeight: 900,
                  color: COLORS.white,
                  fontFamily: 'Inter, system-ui, sans-serif',
                  opacity: 0.8,
                }}
              >
                {Math.floor(fill)}%
              </div>
            </div>

            {/* Status badges */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {['FOB Ningbo', 'En Tránsito', 'ETA: 15 días'].map((s, i) => {
                const colors = [COLORS.cyan, COLORS.emerald, COLORS.amber];
                return (
                  <div
                    key={s}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 100,
                      fontSize: 9,
                      fontWeight: 500,
                      border: `1px solid ${colors[i]}40`,
                      color: colors[i],
                      fontFamily: 'Inter, system-ui, sans-serif',
                      opacity: interpolate(frame, [140 + i * 12, 152 + i * 12], [0, 1], {
                        extrapolateRight: 'clamp',
                        extrapolateLeft: 'clamp',
                      }),
                    }}
                  >
                    {s}
                  </div>
                );
              })}
            </div>

            {/* Cost breakdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {importCosts.map((c, i) => (
                <div
                  key={c.label}
                  style={{
                    opacity: costReveal(i),
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontFamily: 'Inter, system-ui, sans-serif',
                    padding: '3px 0',
                  }}
                >
                  <span style={{ color: COLORS.gray }}>{c.label}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: COLORS.white, fontWeight: 500 }}>{c.value}</span>
                    {c.pct && <span style={{ color: COLORS.grayDark, fontSize: 9 }}>{c.pct}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div
              style={{
                opacity: totalReveal,
                marginTop: 8,
                borderTop: `1px solid ${COLORS.grayDark}30`,
                paddingTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLORS.gray,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Costo Landed
              </span>
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: COLORS.emerald,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                $45,280
              </span>
            </div>
          </div>
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.MONITOR} />
    </AbsoluteFill>
  );
};
